import { removeIncompatibleSearchParameters } from "@helpers/removeIncompatibleSearchParameters"
import { DisplayValue, SHOW_DATA_SOURCE, SHOW_TOTAL } from "@sop/FuelPage/Total/types"

import { createFileRoute } from "@tanstack/react-router"
import { z } from "zod"

const dateSchema = z.preprocess((val) => {
	if (typeof val === "string") {
		const parsedDate = new Date(val)
		if (!isNaN(parsedDate.getTime())) {
			return parsedDate.getTime()
		}
	} else if (typeof val === "number" && !isNaN(val)) {
		return val
	}
	return undefined
}, z.number().int().min(0).optional())

export const consumptionPageSearchSchema = z
	.object({
		vessel: z.optional(z.string()).catch(""), //TODO: find a way to get list of vessels
		rangeFuelTotal: z.optional(z.string()).catch("custom"),
		tab: z
			.optional(z.enum(["dashboard", "total", "seemp", "timeline", "equipment usage"]))
			.catch("dashboard"),
		toFuelTotal: dateSchema,
		fromFuelTotal: dateSchema,
		// The page-header baseline selector's chosen year — broadcasts to every section's own
		// baseline (see TotalContentV3's modePlotBaselineYears) so the page's state, including
		// which baseline it was last set to, can be reproduced from the URL alone.
		baselineFuelTotal: z.optional(z.coerce.number().int()).catch(undefined),
		showTotal: z.optional(z.nativeEnum(SHOW_TOTAL).catch(SHOW_TOTAL.HIDE)),
		showDataSource: z.optional(z.nativeEnum(SHOW_DATA_SOURCE).catch(SHOW_DATA_SOURCE.HIDE)),
		displayVal: z.optional(z.nativeEnum(DisplayValue)).catch(DisplayValue.FUEL),
	})
	.superRefine((data, ctx) => {
		let hasErrors = false
		if (
			(data.fromFuelTotal !== undefined && data.toFuelTotal === undefined) ||
			(data.fromFuelTotal === undefined && data.toFuelTotal !== undefined)
		) {
			ctx.addIssue({
				code: z.ZodIssueCode.custom,
				message: "Both 'fromFuelTotal' and 'toFuelTotal' must be provided together",
				path: ["fromFuelTotal"],
			})
			hasErrors = true
		}

		if (
			data.fromFuelTotal !== undefined &&
			data.toFuelTotal !== undefined &&
			data.fromFuelTotal > data.toFuelTotal
		) {
			ctx.addIssue({
				code: z.ZodIssueCode.custom,
				message: "'fromFuelTotal' must be before or equal to 'toFuelTotal'",
				path: ["fromFuelTotal"],
			})
			hasErrors = true
		}

		if (hasErrors === true) {
			data.fromFuelTotal = undefined
			data.toFuelTotal = undefined
			data.rangeFuelTotal = "last 4 quarters"
		} else if (data.fromFuelTotal !== undefined && data.toFuelTotal !== undefined) {
			data.rangeFuelTotal = "custom"
		}
	})

export const validateConsumptionSearchParams = (
	inputParams: Record<string, unknown>
): z.infer<typeof consumptionPageSearchSchema> => {
	const result = consumptionPageSearchSchema.safeParse(inputParams)

	if (result.success === false) {
		return {
			...inputParams,
			fromFuelTotal: undefined,
			toFuelTotal: undefined,
			rangeFuelTotal: "last 4 quarters",
		}
	}

	return { ...inputParams, ...result.data }
}

export const Route = createFileRoute("/clients/$clientName/_sopPageLayout/consumption")({
	component: RouteComponent,
	validateSearch: validateConsumptionSearchParams,
	search: {
		middlewares: [removeIncompatibleSearchParameters(consumptionPageSearchSchema)],
	},
})

function RouteComponent() {
	return null
}
